/**
 * "Bake plans" section for the product item drawer.
 *
 * Collapsible section listing the bake/monitoring plans of feature flags linked
 * to this product (feature_flags.product_card_id) that have one stored — the
 * telemetry plan a flag carries while baking (and retains as history after it
 * graduates). Rendered as markdown. Superadmin only, like the flags section.
 */
import React from 'react';
import { CollapsibleSection, Flex, Markdown, TagBadge, Text } from '@chatsdet/ui';
import { Fire } from '@phosphor-icons/react';
import { useAuth } from '../../../contexts/AuthContext';
import { useProductCardFlagsQuery } from '../../../ui/pages/superadmin/useSuperadminData';

interface Props {
  productItemId: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const DEFAULT_BAKE_MAX_WINDOW_DAYS = 30;

/** Bake age (days) + burning state from a flag's bake metadata. */
function bakeStatus(flag: {
  bakeStartedAt: string | null;
  bakeMaxWindowDays: number | null;
}): { ageDays: number; burning: boolean } | null {
  if (!flag.bakeStartedAt) return null;
  const ageMs = Date.now() - new Date(flag.bakeStartedAt).getTime();
  const maxWindow = flag.bakeMaxWindowDays ?? DEFAULT_BAKE_MAX_WINDOW_DAYS;
  return { ageDays: Math.floor(ageMs / DAY_MS), burning: ageMs > maxWindow * DAY_MS };
}

export function ProductItemBakePlansSection({ productItemId }: Props) {
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;

  const flagsQuery = useProductCardFlagsQuery(isSuperadmin ? productItemId : null);

  if (!isSuperadmin) return null;

  const flagsWithPlans = (flagsQuery.data?.flags ?? []).filter(
    (flag) => flag.bakeMonitoringPlan && flag.bakeMonitoringPlan.trim().length > 0
  );

  if (flagsWithPlans.length === 0) return null;

  return (
    <CollapsibleSection
      label="Bake plans"
      icon={<Fire size={14} weight="fill" className="text-amber-500" />}
      count={flagsWithPlans.length}
    >
      <div className="space-y-5">
        {flagsWithPlans.map((flag) => {
          const bake = bakeStatus(flag);
          return (
            <div key={flag.id} className="space-y-2">
              <Flex align="center" gap="xs" wrap>
                <Text size="xs" className="font-mono">
                  {flag.name}
                </Text>
                {flag.lifecycleStage === 'baking' && bake && (
                  <TagBadge
                    name={`baked ${bake.ageDays}d${bake.burning ? ' · 🔥 burning' : ''}`}
                    color={bake.burning ? 'red' : 'blue'}
                    hideDot
                    size="sm"
                  />
                )}
                {flag.lifecycleStage !== 'baking' && (
                  <Text size="xs" className="text-muted-foreground">
                    (not baking — plan retained as history)
                  </Text>
                )}
              </Flex>
              <Markdown compact className="text-[13px] leading-relaxed">
                {flag.bakeMonitoringPlan ?? ''}
              </Markdown>
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}
